import { useEffect, useState } from "react";
import { Alert, Button } from "@leylion/design-system";
import { getAccessToken, getStoredContext } from "../api/client";
import type { ContextOption } from "../api/types";
import {
  getPlatformIdentityStatus,
  selectPlatformContext,
  setPlatformAccessToken
} from "./platform-client";

function sameContext(left: ContextOption | null, right: ContextOption | null): boolean {
  if (left === right) return true;
  if (!left || !right) return false;
  return left.tenantId === right.tenantId && left.branchId === right.branchId;
}

export function PlatformContextEntry({
  language,
  onSelected
}: {
  language: "ar" | "en";
  onSelected: (token: string) => void;
}) {
  const [identityToken, setIdentityToken] = useState<string | null>(() => getAccessToken());
  const [context, setContext] = useState<ContextOption | null>(() => getStoredContext());
  const [available, setAvailable] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const syncStoredIdentityAndContext = () => {
      const nextToken = getAccessToken();
      const nextContext = getStoredContext();

      setIdentityToken((current) => current === nextToken ? current : nextToken);
      setContext((current) => sameContext(current, nextContext) ? current : nextContext);
    };

    // App owns the tenant-context state while RootApp owns this platform entry.
    // Keep only the local browser snapshot synchronized across that sibling
    // boundary. This performs no API polling; platform availability is queried
    // only when an identity token exists and no tenant context is selected.
    syncStoredIdentityAndContext();
    const timer = window.setInterval(syncStoredIdentityAndContext, 250);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    if (!identityToken || context) {
      setAvailable(false);
      return;
    }

    let cancelled = false;
    getPlatformIdentityStatus(identityToken)
      .then((status) => {
        if (!cancelled) setAvailable(status.available && status.role === "PlatformOwner");
      })
      .catch(() => {
        if (!cancelled) setAvailable(false);
      });
    return () => { cancelled = true; };
  }, [identityToken, context?.tenantId, context?.branchId]);

  if (!identityToken || context || !available) return null;

  const openPlatform = async () => {
    setBusy(true);
    setError("");
    try {
      const platform = await selectPlatformContext(identityToken);
      setPlatformAccessToken(platform.accessToken);
      onSelected(platform.accessToken);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Platform context could not be selected.");
    } finally {
      setBusy(false);
    }
  };

  const ar = language === "ar";
  return (
    <div className="platform-context-entry" data-testid="platform-context-entry">
      <div>
        <span>PLATFORM OWNER</span>
        <strong>Leylion Platform Console</strong>
        <small>{ar
          ? "إدارة مركزية للمنصة منفصلة بالكامل عن بيانات المؤسسة والفرع."
          : "Central platform control, fully separated from tenant and branch data."}</small>
      </div>
      <Button type="button" data-testid="select-platform-context" disabled={busy} onClick={() => void openPlatform()}>
        {busy ? "…" : ar ? "فتح منصة الإدارة" : "Open Platform Console"}
      </Button>
      {error ? <Alert tone="danger" title={ar ? "تعذر فتح المنصة" : "Platform access failed"}>{error}</Alert> : null}
    </div>
  );
}
